// Drawer "Guardar prospecto y salir" (F2-P) — overlay transversal de los pasos 02–09: motivo
// filtrado por paso + próximo contacto (default mañana) + notas ≤200. El body lo arma
// `buildCreateProspectRequest`, que solo declara campos del DTO del silo.
//
// El silo guarda UN prospecto abierto por teléfono y tienda: guardar de nuevo sobreescribe el que
// existe, así que el drawer lo lee y lo precarga en vez de dejar que el vendedor pise a ciegas.
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { Modal, Button, Input, Select } from '@/kit';
import { useCreateProspect, useProspectByPhone, useVerifiedName } from '../api/useWizard';
import { peekLinkedSku } from '../linkedSku';
import {
    buildCreateProspectRequest,
    isPendingReason,
    prospectFormPrefill,
    reasonOptionsForStep,
} from '../prospects';
import { stepBySlug } from '../steps';
import { mxDayOffset } from '@/lib/dateHelpers';
import type { PendingReason, WizardSession, WizardStepSlug } from '../types';

export function ProspectDrawer({
    session,
    currentSlug,
    open,
    onClose,
}: {
    session: WizardSession;
    /** Pantalla visible: a ella vuelve la venta al reanudar. */
    currentSlug?: WizardStepSlug;
    open: boolean;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const createProspect = useCreateProspect(session.sellerId);
    const lookup = useProspectByPhone(session.phone, open);
    const saved = lookup.data?.found === true ? lookup.data : null;
    // Solo hace falta si la sesión no lo trae, y antes del KYC no existe.
    const kycName = useVerifiedName(
        session.sessionId,
        open && !session.kyc.verified?.fullName && currentSlug !== 'otp',
    );
    const sessionName = session.kyc.verified?.fullName ?? kycName ?? session.firstName ?? '';
    // El sku ligado se lee al montar: «Elige el teléfono» lo borra al entrar.
    const [linkedSku] = useState(() => peekLinkedSku(session.sessionId));

    const [customerName, setCustomerName] = useState(sessionName);
    const [reason, setReason] = useState<PendingReason | ''>('');
    const [freeReason, setFreeReason] = useState('');
    // Mañana en el piso de venta. En UTC, una venta de las 19:00 en CDMX agendaría pasado mañana.
    const [nextContact, setNextContact] = useState(() => mxDayOffset(-1));
    const [notes, setNotes] = useState('');
    const [rescheduled, setRescheduled] = useState(false);

    // La pantalla visible manda: `currentStep` es el último paso COMPLETADO, no donde está el
    // vendedor. El motivo elegido manda sobre el guardado: el drawer no se desmonta entre pasos.
    const visibleStep = (currentSlug && stepBySlug(currentSlug)?.id) || session.currentStep;
    const reasons = reasonOptionsForStep(
        visibleStep,
        reason !== '' ? reason : (saved?.pendingReason ?? undefined),
    );

    // La precarga corre una sola vez y solo sobre un formulario intacto: la lectura puede tardar y
    // el vendedor ya estar tecleando, y llegar entonces le borraría lo escrito.
    const touched = useRef(false);
    const prefilled = useRef(false);
    const markTouched = (): void => {
        touched.current = true;
    };

    // El nombre del cliente lo sabemos recién después del KYC, y el drawer NO se desmonta entre
    // pasos: sin esto, abrirlo en el paso 01 lo deja vacío para el resto de la venta.
    useEffect(() => {
        if (touched.current || !sessionName) return;
        setCustomerName((prev) => (prev.trim() === '' ? sessionName : prev));
    }, [sessionName]);

    useEffect(() => {
        if (!saved || prefilled.current || touched.current) return;
        prefilled.current = true;
        const prefill = prospectFormPrefill(saved, mxDayOffset(0));
        // Lo que el prospecto no trae se queda con lo que ya había: el nombre de la sesión y el
        // default de mañana.
        setCustomerName((prev) => prefill.name || prev);
        setReason(prefill.pendingReason);
        setFreeReason(prefill.freeReason);
        setNextContact((prev) => prefill.nextContactDay ?? prev);
        setNotes(prefill.notes);
        setRescheduled(prefill.scheduleWasOverdue);
    }, [saved]);

    const canSave =
        reason !== '' &&
        (reason !== 'OTHER' || freeReason.trim() !== '') &&
        customerName.trim() !== '';

    const salirSinGuardar = async (): Promise<void> => {
        onClose();
        await navigate({ to: '/sales' });
    };

    const handleSave = async (): Promise<void> => {
        if (reason === '') return;
        try {
            await createProspect.mutateAsync(
                buildCreateProspectRequest({
                    name: customerName,
                    phone: session.phone,
                    pendingReason: reason,
                    freeReason,
                    nextContactDay: nextContact,
                    notes,
                    productSku: session.product?.sku ?? linkedSku,
                    // El vínculo de vuelta: sin él «Reabrir» no encuentra esta venta.
                    wizardSessionId: session.sessionId,
                    resumeSlug: currentSlug,
                    // Mandar los vacíos solo si el vendedor VIO lo guardado: si no, borraría
                    // campos que nunca se pintaron.
                    overwritesExisting: prefilled.current,
                }),
            );
        } catch {
            // El drawer queda abierto para que el vendedor reintente sin volver a teclear todo.
            toast.error('No se pudo guardar el prospecto. Intenta de nuevo.');
            return;
        }
        toast.success('Prospecto guardado. Puedes retomarla en 24 h.');
        onClose();
        await navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { tab: 'prospects' },
        });
    };

    return (
        <Modal isOpen={open} onClose={onClose} title="Guardar prospecto y salir">
            <div className="flex flex-col gap-3">
                {lookup.isLoading && (
                    <p className="text-[12px] text-muted-foreground">
                        Buscando el prospecto de este teléfono…
                    </p>
                )}
                {saved && (
                    <p className="text-[12px] text-muted-foreground">
                        Ya tienes un prospecto guardado con este teléfono. Al guardar, se actualiza
                        ese mismo.
                    </p>
                )}
                <Input
                    label="Cliente"
                    value={customerName}
                    onChange={(e) => {
                        markTouched();
                        setCustomerName(e.target.value);
                    }}
                    placeholder="Nombre del cliente"
                />
                {/* Sin opción vacía: Radix revienta con un `SelectItem` de value ''. El vacío es el
                    placeholder del trigger. */}
                <Select
                    label="Motivo"
                    value={reason}
                    placeholder="Selecciona un motivo…"
                    onChange={(value) => {
                        markTouched();
                        if (isPendingReason(value)) setReason(value);
                    }}
                    options={reasons}
                />
                {reason === 'OTHER' && (
                    <Input
                        label="¿Cuál?"
                        value={freeReason}
                        maxLength={200}
                        onChange={(e) => {
                            markTouched();
                            setFreeReason(e.target.value);
                        }}
                    />
                )}
                <Input
                    label="Próximo contacto"
                    type="date"
                    value={nextContact}
                    min={mxDayOffset(0)}
                    onChange={(e) => {
                        markTouched();
                        setRescheduled(false);
                        setNextContact(e.target.value);
                    }}
                />
                {rescheduled && (
                    <p className="-mt-2 text-[12px] text-muted-foreground">
                        La fecha que tenía ya pasó, la movimos a hoy.
                    </p>
                )}
                <div>
                    <label
                        className="mb-1 block text-[12px] font-medium text-foreground/80"
                        htmlFor="prospect-notes"
                    >
                        Notas
                    </label>
                    <textarea
                        id="prospect-notes"
                        value={notes}
                        maxLength={200}
                        rows={3}
                        onChange={(e) => {
                            markTouched();
                            setNotes(e.target.value);
                        }}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px]"
                        placeholder="Notas (máx. 200)"
                    />
                </div>
                <div className="flex items-center gap-2 pt-1">
                    {/* Escape: la flecha «Salir de la venta» también abre este modal, y sin salida
                        propia el vendedor quedaba obligado a inventar un motivo para poder irse. */}
                    <button
                        type="button"
                        disabled={createProspect.isPending}
                        onClick={() => void salirSinGuardar()}
                        className="mr-auto text-[12px] text-muted-foreground underline decoration-dotted transition-colors hover:text-foreground disabled:opacity-50"
                    >
                        Salir sin guardar
                    </button>
                    <Button variant="ghost" disabled={createProspect.isPending} onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        isLoading={createProspect.isPending}
                        onClick={() => void handleSave()}
                        disabled={!canSave}
                    >
                        Guardar prospecto
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
